import { useEffect, useRef, useState } from 'react';

/** Few enough to still read as a waveform in the narrowest thread. */
const MIN_BARS = 8;

/**
 * How many bars fit the space a waveform actually got.
 *
 * A fixed count either bunches up in one corner of a wide thread or overflows a
 * narrow one - and the width is never known in advance: it is whatever the
 * button, the timer and the icons beside it leave over, in a bubble that is
 * itself shrink-to-fit inside a percentage-capped column.
 *
 * Both waveforms in the feature need this now - the recorder's live bars and
 * the voice note's scrubber - which is what moved it out of the recorder.
 *
 * @param pitch one bar plus the space beside it, at the density that waveform draws
 * @param enabled false while the element is unmounted, so the observer is not
 *   attached to a ref that will never be filled
 */
export const useBarCount = (pitch: number, enabled = true) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState(24);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() =>
      setCount(Math.max(MIN_BARS, Math.floor(element.clientWidth / pitch)))
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [pitch, enabled]);

  return { ref, count };
};
